/**
 * FantasticFractals - Core Fractal Mathematics Engine
 * High-performance pure computation algorithms compatible with both Browser & Edge Workers.
 * Features 27 famous escape-time, convergence, non-analytic, and geometric fractals.
 */

export const FRACTAL_TYPES = [
  // 1. Classical Escape-Time
  {
    id: 'mandelbrot',
    name: 'Mandelbrot Set',
    category: 'Escape-time Complex',
    formula: 'z_{n+1} = z_n^2 + c',
    description: 'The iconic boundary of stability in the complex quadratic polynomial family.',
    defaultCenter: { x: -0.7, y: 0.0 },
    defaultZoom: 1.0,
    defaultIterations: 120,
    maxIterations: 2000,
  },
  {
    id: 'julia',
    name: 'Julia Set',
    category: 'Escape-time Complex',
    formula: 'z_{n+1} = z_n^2 + C_{fixed}',
    description: 'Dynamical boundary for a fixed complex constant C. Produces infinite dendritic patterns.',
    defaultCenter: { x: 0.0, y: 0.0 },
    defaultZoom: 1.0,
    defaultIterations: 140,
    maxIterations: 2000,
    supportsJuliaConst: true,
    defaultJulia: { r: -0.7, i: 0.27015 },
  },

  // 2. Non-Analytic & Absolute Value
  {
    id: 'burning_ship',
    name: 'Burning Ship',
    category: 'Non-Analytic',
    formula: 'z_{n+1} = (|Re(z)| + i|Im(z)|)^2 + c',
    description: 'Non-analytic fractal revealing sharp masts, sails, and dark burning flames along the real axis.',
    defaultCenter: { x: -0.45, y: -0.5 },
    defaultZoom: 1.0,
    defaultIterations: 150,
    maxIterations: 2000,
  },
  {
    id: 'celtic_mandelbrot',
    name: 'Celtic Mandelbrot',
    category: 'Non-Analytic',
    formula: 'z_{n+1} = |Re(z_n^2)| + i Im(z_n^2) + c',
    description: 'Intricate woven knotwork fractal generated by taking absolute value of real component.',
    defaultCenter: { x: -0.5, y: 0.0 },
    defaultZoom: 1.0,
    defaultIterations: 130,
    maxIterations: 2000,
  },
  {
    id: 'buffalo',
    name: 'Buffalo Fractal',
    category: 'Non-Analytic',
    formula: 'z_{n+1} = |Re(z_n^2)| - |Re(z_n)| + c',
    description: 'Distinctive horned silhouette with aggressive, hyper-complex tendril foliage.',
    defaultCenter: { x: -0.5, y: 0.0 },
    defaultZoom: 1.0,
    defaultIterations: 140,
    maxIterations: 2000,
  },
  {
    id: 'perp_mandelbrot',
    name: 'Perpendicular Mandelbrot',
    category: 'Non-Analytic',
    formula: 'z_{n+1} = Re(z_n)^2 - Im(z_n)^2 - 2i|Re(z_n)|Im(z_n) + c',
    description: 'Asymmetric hybrid displaying sharp vertical razor filaments across one half-plane.',
    defaultCenter: { x: -0.5, y: 0.0 },
    defaultZoom: 1.0,
    defaultIterations: 140,
    maxIterations: 2000,
  },
  {
    id: 'perp_burning_ship',
    name: 'Perpendicular Burning Ship',
    category: 'Non-Analytic',
    formula: 'z_{n+1} = |Re(z_n)|^2 - Im(z_n)^2 - 2i Re(z_n)|Im(z_n)| + c',
    description: 'Cross-breed of Burning Ship and perpendicular reflections, forming soaring cathedral arches.',
    defaultCenter: { x: -0.45, y: -0.3 },
    defaultZoom: 1.0,
    defaultIterations: 140,
    maxIterations: 2000,
  },

  // 3. Conjugate & Higher Order Polynomials
  {
    id: 'tricorn',
    name: 'Tricorn (Mandelbar)',
    category: 'Conjugate Escape',
    formula: 'z_{n+1} = \\bar{z}_n^2 + c',
    description: 'Generated using complex conjugation. Possesses 3-fold cardioid rotational symmetry.',
    defaultCenter: { x: 0.0, y: 0.0 },
    defaultZoom: 0.9,
    defaultIterations: 120,
    maxIterations: 2000,
  },
  {
    id: 'multibar3',
    name: 'Cubic Tricorn (Multibar 3)',
    category: 'Conjugate Escape',
    formula: 'z_{n+1} = \\bar{z}_n^3 + c',
    description: 'Cubic conjugate Mandelbrot showing four-petaled anti-holomorphic symmetry.',
    defaultCenter: { x: 0.0, y: 0.0 },
    defaultZoom: 0.9,
    defaultIterations: 120,
    maxIterations: 2000,
  },
  {
    id: 'multibrot3',
    name: 'Multibrot (d=3)',
    category: 'Higher Order Polynomial',
    formula: 'z_{n+1} = z_n^3 + c',
    description: 'Cubic Mandelbrot set exhibiting 2-lobed symmetry and hyper-dense filament webs.',
    defaultCenter: { x: 0.0, y: 0.0 },
    defaultZoom: 0.9,
    defaultIterations: 120,
    maxIterations: 2000,
  },
  {
    id: 'multibrot4',
    name: 'Multibrot (d=4)',
    category: 'Higher Order Polynomial',
    formula: 'z_{n+1} = z_n^4 + c',
    description: 'Quartic Mandelbrot set showing 3 symmetrical primary bulbs and ornate tendrils.',
    defaultCenter: { x: 0.0, y: 0.0 },
    defaultZoom: 0.9,
    defaultIterations: 120,
    maxIterations: 2000,
  },

  // 4. Convergence & Relaxation
  {
    id: 'newton',
    name: 'Newton-Raphson (z³ - 1 = 0)',
    category: 'Convergence Basin',
    formula: 'z_{n+1} = z_n - \\frac{z_n^3 - 1}{3z_n^2}',
    description: 'Chaotic boundaries separating basins of attraction toward cube roots of unity.',
    defaultCenter: { x: 0.0, y: 0.0 },
    defaultZoom: 0.8,
    defaultIterations: 80,
    maxIterations: 1000,
  },
  {
    id: 'newton_z4',
    name: 'Newton Quartic (z⁴ - 1 = 0)',
    category: 'Convergence Basin',
    formula: 'z_{n+1} = \\frac{3z_n + 1/z_n^3}{4}',
    description: 'Four orthogonal basins of attraction with 4-fold cross fractal boundaries.',
    defaultCenter: { x: 0.0, y: 0.0 },
    defaultZoom: 0.8,
    defaultIterations: 80,
    maxIterations: 1000,
  },
  {
    id: 'nova',
    name: 'Nova Fractal',
    category: 'Convergence Basin',
    formula: 'z_{n+1} = z_n - R \\frac{z_n^3 - 1}{3z_n^2} + c',
    description: 'A hybrid relaxation method adding the coordinate c at each Newton iteration step.',
    defaultCenter: { x: 0.0, y: 0.0 },
    defaultZoom: 0.8,
    defaultIterations: 90,
    maxIterations: 1000,
  },

  // 5. Physics & Rational Maps
  {
    id: 'magnet1',
    name: 'Magnet Fractal Type 1',
    category: 'Physics & Rational',
    formula: 'z_{n+1} = \\left(\\frac{z_n^2 + c - 1}{2z_n + c - 2}\\right)^2',
    description: 'Renormalization transformation for the Potts spin glass model in statistical physics.',
    defaultCenter: { x: 0.5, y: 0.0 },
    defaultZoom: 0.9,
    defaultIterations: 100,
    maxIterations: 1500,
  },
  {
    id: 'magnet2',
    name: 'Magnet Fractal Type 2',
    category: 'Physics & Rational',
    formula: 'z_{n+1} = \\left(\\frac{z_n^3 + 3(c-1)z_n + (c-1)(c-2)}{3z_n^2 + 3(c-2)z_n + (c-1)(c-2) + 1}\\right)^2',
    description: 'Higher-order magnetic phase transition rational map producing intricate coral lattices.',
    defaultCenter: { x: 0.5, y: 0.0 },
    defaultZoom: 0.9,
    defaultIterations: 90,
    maxIterations: 1200,
  },
  {
    id: 'feather',
    name: 'Feather Fractal',
    category: 'Physics & Rational',
    formula: 'z_{n+1} = \\frac{z_n^3}{1 + z_n^2} + c',
    description: 'Rational function creating delicate plumes and crystalline barbs.',
    defaultCenter: { x: 0.0, y: 0.0 },
    defaultZoom: 0.8,
    defaultIterations: 100,
    maxIterations: 1500,
  },

  // 6. Geometric & Recursive IFS
  {
    id: 'sierpinski_triangle',
    name: 'Sierpinski Triangle (Gasket)',
    category: 'Geometric / IFS',
    formula: 'p_{n+1} = 2p_n - v_i',
    description: 'Classic self-similar triangular fractal with Hausdorff dimension log_2(3) ~ 1.585.',
    defaultCenter: { x: 0.0, y: 0.25 },
    defaultZoom: 0.85,
    defaultIterations: 8,
    maxIterations: 14,
  },
  {
    id: 'koch_snowflake',
    name: 'Koch Snowflake',
    category: 'Geometric / IFS',
    formula: 'K_{n+1} = K_n \\cup \\text{triangles}',
    description: 'Infinite perimeter enclosing finite area. Continuous everywhere but nowhere differentiable.',
    defaultCenter: { x: 0.0, y: 0.0 },
    defaultZoom: 0.85,
    defaultIterations: 4,
    maxIterations: 8,
  },
  {
    id: 'vicsek',
    name: 'Vicsek Fractal (Box Cross)',
    category: 'Geometric / IFS',
    formula: '(mx \\neq 1) \\land (my \\neq 1) \\implies \\text{cut}',
    description: 'Planar box fractal cross constructed by recursively partitioning into 9 squares and keeping 5.',
    defaultCenter: { x: 0.5, y: 0.5 },
    defaultZoom: 1.0,
    defaultIterations: 6,
    maxIterations: 10,
  },
  {
    id: 'sierpinski_carpet',
    name: 'Sierpinski Carpet',
    category: 'Geometric / IFS',
    formula: '(x \\pmod 3 == 1) \\land (y \\pmod 3 == 1)',
    description: 'Classic planar square matrix with fractional Hausdorff dimension ~1.8928.',
    defaultCenter: { x: 0.5, y: 0.5 },
    defaultZoom: 1.0,
    defaultIterations: 4,
    maxIterations: 8,
  },
  {
    id: 'cantor_dust',
    name: 'Cantor Dust (2D)',
    category: 'Geometric / IFS',
    formula: '(mx == 1) \\lor (my == 1) \\implies \\text{cut}',
    description: 'Cartesian product of the Cantor ternary set C x C with Lebesgue measure zero.',
    defaultCenter: { x: 0.5, y: 0.5 },
    defaultZoom: 1.0,
    defaultIterations: 6,
    maxIterations: 10,
  },
  {
    id: 'apollonian',
    name: 'Apollonian Inversion Gasket',
    category: 'Geometric / IFS',
    formula: 'p_{n+1} = \\frac{p_n}{|p_n|^2}',
    description: 'Hypnotic fractal generated by infinite mutual osculating circle inversions.',
    defaultCenter: { x: 0.0, y: 0.0 },
    defaultZoom: 0.9,
    defaultIterations: 14,
    maxIterations: 30,
  },
  {
    id: 'dragon_curve',
    name: 'Heighway Dragon Boundary',
    category: 'Geometric / IFS',
    formula: 'z_{n+1} = \\frac{z_n}{\\sqrt{2}} e^{i\\pi/4} + c',
    description: 'Boundary limit of the paper-folding Heighway dragon curve.',
    defaultCenter: { x: 0.0, y: 0.0 },
    defaultZoom: 0.9,
    defaultIterations: 16,
    maxIterations: 28,
  },

  // 7. Dynamical & Discrete Systems
  {
    id: 'collatz',
    name: 'Collatz Complex Fractal',
    category: 'Dynamical Systems',
    formula: 'f(z) = \\frac{1}{4}(1 + 4z - (1 + 2z)\\cos(\\pi z))',
    description: 'Smooth extension of the famous 3n + 1 Collatz conjecture into the complex plane.',
    defaultCenter: { x: 0.0, y: 0.0 },
    defaultZoom: 0.4,
    defaultIterations: 60,
    maxIterations: 600,
  },
  {
    id: 'lyapunov',
    name: 'Lyapunov Space (AB)',
    category: 'Dynamical Systems',
    formula: 'x_{n+1} = r_n x_n (1 - x_n)',
    description: 'Measures stability exponents of alternating population growth rates along the A and B axes.',
    defaultCenter: { x: 3.2, y: 3.2 },
    defaultZoom: 1.0,
    defaultIterations: 100,
    maxIterations: 800,
  },
  {
    id: 'barnsley_fern',
    name: 'Barnsley Fern IFS',
    category: 'Dynamical Systems',
    formula: 'x_{n+1} = A_i x_n + b_i',
    description: 'Stochastic IFS modeling self-similar biological fern fronds using affine transformations.',
    defaultCenter: { x: 0.0, y: 5.0 },
    defaultZoom: 0.8,
    defaultIterations: 200000,
    maxIterations: 2000000,
    isPointFractal: true,
  }
];

export function getFractalMeta(typeId) {
  return FRACTAL_TYPES.find((f) => f.id === typeId) || FRACTAL_TYPES[0];
}

/**
 * Computes a single pixel for escape-time and convergence fractals (CPU Engine)
 */
export function computePoint(type, x0, y0, maxIter, params = {}) {
  const BAILOUT_SQ = 16.0;
  const LOG2 = Math.LN2;

  switch (type) {
    case 'mandelbrot': {
      const q = (x0 - 0.25) * (x0 - 0.25) + y0 * y0;
      if (q * (q + (x0 - 0.25)) <= 0.25 * y0 * y0 || (x0 + 1.0) * (x0 + 1.0) + y0 * y0 <= 0.0625) return -1;
      let zx = 0.0, zy = 0.0, zx2 = 0.0, zy2 = 0.0, i = 0;
      while (zx2 + zy2 <= BAILOUT_SQ && i < maxIter) {
        zy = 2.0 * zx * zy + y0;
        zx = zx2 - zy2 + x0;
        zx2 = zx * zx;
        zy2 = zy * zy;
        i++;
      }
      if (i >= maxIter) return -1;
      const mod = Math.sqrt(zx2 + zy2);
      return Math.max(0, i + 1.0 - Math.log(Math.log(mod) / LOG2) / LOG2);
    }

    case 'julia': {
      const cr = params.juliaReal ?? -0.7;
      const ci = params.juliaImag ?? 0.27015;
      let zx = x0, zy = y0, zx2 = zx * zx, zy2 = zy * zy, i = 0;
      while (zx2 + zy2 <= BAILOUT_SQ && i < maxIter) {
        zy = 2.0 * zx * zy + ci;
        zx = zx2 - zy2 + cr;
        zx2 = zx * zx;
        zy2 = zy * zy;
        i++;
      }
      if (i >= maxIter) return -1;
      const mod = Math.sqrt(zx2 + zy2);
      return Math.max(0, i + 1.0 - Math.log(Math.log(mod) / LOG2) / LOG2);
    }

    case 'burning_ship': {
      let zx = x0, zy = y0, zx2 = zx * zx, zy2 = zy * zy, i = 0;
      while (zx2 + zy2 <= BAILOUT_SQ && i < maxIter) {
        zy = Math.abs(2.0 * zx * zy) - y0;
        zx = zx2 - zy2 - x0;
        zx2 = zx * zx;
        zy2 = zy * zy;
        i++;
      }
      if (i >= maxIter) return -1;
      const mod = Math.sqrt(zx2 + zy2);
      return Math.max(0, i + 1.0 - Math.log(Math.log(mod) / LOG2) / LOG2);
    }

    case 'celtic_mandelbrot': {
      let zx = 0.0, zy = 0.0, i = 0;
      while (zx * zx + zy * zy <= BAILOUT_SQ && i < maxIter) {
        const nextX = Math.abs(zx * zx - zy * zy) + x0;
        const nextY = 2.0 * zx * zy + y0;
        zx = nextX; zy = nextY;
        i++;
      }
      return i >= maxIter ? -1 : i;
    }

    case 'buffalo': {
      let zx = 0.0, zy = 0.0, i = 0;
      while (zx * zx + zy * zy <= BAILOUT_SQ && i < maxIter) {
        const nextX = Math.abs(zx * zx - zy * zy) - Math.abs(zx) + x0;
        const nextY = -Math.abs(2.0 * zx * zy) + y0;
        zx = nextX; zy = nextY;
        i++;
      }
      return i >= maxIter ? -1 : i;
    }

    case 'perp_mandelbrot': {
      let zx = 0.0, zy = 0.0, i = 0;
      while (zx * zx + zy * zy <= BAILOUT_SQ && i < maxIter) {
        const nextX = zx * zx - zy * zy + x0;
        const nextY = -2.0 * Math.abs(zx) * zy + y0;
        zx = nextX; zy = nextY;
        i++;
      }
      return i >= maxIter ? -1 : i;
    }

    case 'perp_burning_ship': {
      let zx = 0.0, zy = 0.0, i = 0;
      while (zx * zx + zy * zy <= BAILOUT_SQ && i < maxIter) {
        const nextX = zx * zx - zy * zy + x0;
        const nextY = -2.0 * zx * Math.abs(zy) + y0;
        zx = nextX; zy = nextY;
        i++;
      }
      return i >= maxIter ? -1 : i;
    }

    case 'tricorn': {
      let zx = 0.0, zy = 0.0, zx2 = 0.0, zy2 = 0.0, i = 0;
      while (zx2 + zy2 <= BAILOUT_SQ && i < maxIter) {
        zy = -2.0 * zx * zy + y0;
        zx = zx2 - zy2 + x0;
        zx2 = zx * zx;
        zy2 = zy * zy;
        i++;
      }
      if (i >= maxIter) return -1;
      const mod = Math.sqrt(zx2 + zy2);
      return Math.max(0, i + 1.0 - Math.log(Math.log(mod) / LOG2) / LOG2);
    }

    case 'multibar3': {
      let zx = 0.0, zy = 0.0, i = 0;
      while (zx * zx + zy * zy <= BAILOUT_SQ && i < maxIter) {
        const nextX = zx * (zx * zx - 3.0 * zy * zy) + x0;
        const nextY = -zy * (3.0 * zx * zx - zy * zy) + y0;
        zx = nextX; zy = nextY;
        i++;
      }
      return i >= maxIter ? -1 : i;
    }

    case 'multibrot3': {
      let zx = 0.0, zy = 0.0, i = 0;
      while (zx * zx + zy * zy <= BAILOUT_SQ && i < maxIter) {
        const nextX = zx * (zx * zx - 3.0 * zy * zy) + x0;
        const nextY = zy * (3.0 * zx * zx - zy * zy) + y0;
        zx = nextX; zy = nextY;
        i++;
      }
      return i >= maxIter ? -1 : i;
    }

    case 'multibrot4': {
      let zx = 0.0, zy = 0.0, i = 0;
      while (zx * zx + zy * zy <= BAILOUT_SQ && i < maxIter) {
        const zx2 = zx * zx - zy * zy;
        const zy2 = 2.0 * zx * zy;
        const nextX = zx2 * zx2 - zy2 * zy2 + x0;
        const nextY = 2.0 * zx2 * zy2 + y0;
        zx = nextX; zy = nextY;
        i++;
      }
      return i >= maxIter ? -1 : i;
    }

    case 'newton': {
      let zx = x0, zy = y0, i = 0;
      const tolerance = 1e-4;
      while (i < maxIter) {
        const d2 = zx * zx + zy * zy;
        if (d2 < 1e-8) return -1;
        const invD4 = 1.0 / (d2 * d2);
        const invZ2x = (zx * zx - zy * zy) * invD4;
        const invZ2y = (-2.0 * zx * zy) * invD4;
        const nextX = (2.0 * zx + invZ2x) / 3.0;
        const nextY = (2.0 * zy + invZ2y) / 3.0;
        const dx = nextX - zx, dy = nextY - zy;
        if (dx * dx + dy * dy < tolerance) {
          const rootId = nextX > 0.5 ? 1 : nextY > 0 ? 2 : 3;
          return i + rootId * 15;
        }
        zx = nextX; zy = nextY;
        i++;
      }
      return -1;
    }

    case 'newton_z4': {
      let zx = x0, zy = y0, i = 0;
      while (i < maxIter) {
        const d2 = zx * zx + zy * zy;
        if (d2 < 1e-8) break;
        // z - (z^4 - 1)/(4z^3) = (3z + 1/z^3) / 4
        // 1/z^3 = (zx - i zy)^3 / d2^3
        const d6 = d2 * d2 * d2;
        const z3x = (zx * zx * zx - 3.0 * zx * zy * zy) / d6;
        const z3y = (-3.0 * zx * zx * zy + zy * zy * zy) / d6;
        const nextX = (3.0 * zx + z3x) * 0.25;
        const nextY = (3.0 * zy + z3y) * 0.25;
        const dx = nextX - zx, dy = nextY - zy;
        if (dx * dx + dy * dy < 1e-4) {
          const rootId = nextX > 0.5 ? 1 : nextX < -0.5 ? 2 : nextY > 0 ? 3 : 4;
          return i + rootId * 18;
        }
        zx = nextX; zy = nextY;
        i++;
      }
      return -1;
    }

    case 'nova': {
      let zx = x0, zy = y0, i = 0;
      while (i < maxIter) {
        const d2 = zx * zx + zy * zy;
        if (d2 < 1e-8) break;
        const invD4 = 1.0 / (d2 * d2);
        const invZ2x = (zx * zx - zy * zy) * invD4;
        const invZ2y = (-2.0 * zx * zy) * invD4;
        const nextX = (2.0 * zx + invZ2x) / 3.0 + x0 * 0.2;
        const nextY = (2.0 * zy + invZ2y) / 3.0 + y0 * 0.2;
        const dx = nextX - zx, dy = nextY - zy;
        if (dx * dx + dy * dy < 1e-3) return i * 2.5;
        zx = nextX; zy = nextY;
        i++;
      }
      return i >= maxIter ? -1 : i;
    }

    case 'feather': {
      let zx = x0, zy = y0, i = 0;
      while (zx * zx + zy * zy <= BAILOUT_SQ && i < maxIter) {
        // z^3 / (1 + z^2) + c
        const z2x = zx * zx - zy * zy;
        const z2y = 2.0 * zx * zy;
        const denomX = 1.0 + z2x;
        const denomY = z2y;
        const denomD2 = denomX * denomX + denomY * denomY;
        if (denomD2 < 1e-8) break;
        const z3x = zx * z2x - zy * z2y;
        const z3y = zx * z2y + zy * z2x;
        const fracX = (z3x * denomX + z3y * denomY) / denomD2;
        const fracY = (z3y * denomX - z3x * denomY) / denomD2;
        zx = fracX + x0;
        zy = fracY + y0;
        i++;
      }
      return i >= maxIter ? -1 : i;
    }

    case 'magnet1': {
      let zx = 0.0, zy = 0.0, i = 0;
      while (zx * zx + zy * zy <= 100.0 && i < maxIter) {
        // ((z^2 + c - 1) / (2z + c - 2))^2
        const numX = zx * zx - zy * zy + x0 - 1.0;
        const numY = 2.0 * zx * zy + y0;
        const denX = 2.0 * zx + x0 - 2.0;
        const denY = 2.0 * zy + y0;
        const denD2 = denX * denX + denY * denY;
        if (denD2 < 1e-8) break;
        const qx = (numX * denX + numY * denY) / denD2;
        const qy = (numY * denX - numX * denY) / denD2;
        zx = qx * qx - qy * qy;
        zy = 2.0 * qx * qy;
        i++;
      }
      return i >= maxIter ? -1 : i;
    }

    case 'magnet2': {
      let zx = 0.0, zy = 0.0, i = 0;
      while (zx * zx + zy * zy <= 100.0 && i < maxIter) {
        const c1x = x0 - 1.0, c1y = y0;
        const c2x = x0 - 2.0, c2y = y0;
        const c12x = c1x * c2x - c1y * c2y;
        const c12y = c1x * c2y + c1y * c2x;

        const numX = zx * (zx * zx - 3.0 * zy * zy) + 3.0 * (c1x * zx - c1y * zy) + c12x;
        const numY = zy * (3.0 * zx * zx - zy * zy) + 3.0 * (c1x * zy + c1y * zx) + c12y;

        const denX = 3.0 * (zx * zx - zy * zy) + 3.0 * (c2x * zx - c2y * zy) + c12x + 1.0;
        const denY = 6.0 * zx * zy + 3.0 * (c2x * zy + c2y * zx) + c12y;

        const denD2 = denX * denX + denY * denY;
        if (denD2 < 1e-8) break;
        const qx = (numX * denX + numY * denY) / denD2;
        const qy = (numY * denX - numX * denY) / denD2;
        zx = qx * qx - qy * qy;
        zy = 2.0 * qx * qy;
        i++;
      }
      return i >= maxIter ? -1 : i;
    }

    case 'sierpinski_triangle': {
      const v1x = 0.0, v1y = 1.0;
      const v2x = -0.8660254038, v2y = -0.5;
      const v3x = 0.8660254038, v3y = -0.5;

      let px = x0;
      let py = y0;
      let d1 = px * v1x + py * v1y;
      let d2 = px * v2x + py * v2y;
      let d3 = px * v3x + py * v3y;
      const minD = Math.min(d1, d2, d3);

      if (minD < -0.5) {
        const dist = -0.5 - minD;
        return Math.log1p(dist * 30.0) * 20.0 + 1.0;
      }

      const depth = Math.min(maxIter, 14);
      for (let d = 0; d < depth; d++) {
        d1 = px * v1x + py * v1y;
        d2 = px * v2x + py * v2y;
        d3 = px * v3x + py * v3y;
        const maxD = Math.max(d1, d2, d3);
        if (maxD < 0.25) {
          return (d + 1) * 16.0;
        }
        let vx = v1x, vy = v1y;
        if (d2 === maxD) {
          vx = v2x; vy = v2y;
        } else if (d3 === maxD) {
          vx = v3x; vy = v3y;
        }
        px = 2.0 * px - vx;
        py = 2.0 * py - vy;
      }
      return -1;
    }

    case 'koch_snowflake': {
      let px = Math.abs(x0);
      let py = -y0;
      const n1x = 0.5;
      const n1y = -0.8660254038;
      py -= 0.2886751346;
      const d1 = (px - 0.5) * n1x + py * n1y;
      if (d1 > 0) {
        px -= 2.0 * d1 * n1x;
        py -= 2.0 * d1 * n1y;
      }
      const n2x = 0.8660254038;
      const n2y = -0.5;
      px += 0.5;
      let scale = 1.0;
      const depth = Math.max(1, Math.min(maxIter, 8));
      for (let d = 0; d < depth; d++) {
        px *= 3.0;
        py *= 3.0;
        scale *= 3.0;
        px -= 1.5;
        px = Math.abs(px);
        px -= 0.5;
        const d2 = px * n2x + py * n2y;
        if (d2 < 0) {
          px -= 2.0 * d2 * n2x;
          py -= 2.0 * d2 * n2y;
        }
      }
      if (py <= 0) return -1;
      const dist = py / scale;
      return Math.log1p(dist * 60.0) * 22.0 + depth * 12.0;
    }

    case 'vicsek': {
      let cx = Math.abs(x0);
      let cy = Math.abs(y0);
      const depth = Math.min(maxIter, 8);
      for (let d = 0; d < depth; d++) {
        const mx = Math.floor(cx * 3.0) % 3;
        const my = Math.floor(cy * 3.0) % 3;
        // In cross form, removed if neither mx nor my is 1
        if (mx !== 1 && my !== 1) {
          return (d + 1) * 18.0;
        }
        cx = (cx * 3.0) % 1.0;
        cy = (cy * 3.0) % 1.0;
      }
      return -1;
    }

    case 'cantor_dust': {
      let cx = Math.abs(x0);
      let cy = Math.abs(y0);
      const depth = Math.min(maxIter, 8);
      for (let d = 0; d < depth; d++) {
        const mx = Math.floor(cx * 3.0) % 3;
        const my = Math.floor(cy * 3.0) % 3;
        if (mx === 1 || my === 1) {
          return (d + 1) * 16.0;
        }
        cx = (cx * 3.0) % 1.0;
        cy = (cy * 3.0) % 1.0;
      }
      return -1;
    }

    case 'apollonian': {
      let px = x0, py = y0;
      const depth = Math.min(maxIter, 24);
      for (let d = 0; d < depth; d++) {
        px = ((px % 2.0) + 2.0) % 2.0 - 1.0;
        py = ((py % 2.0) + 2.0) % 2.0 - 1.0;
        const r2 = px * px + py * py;
        if (r2 < 1.0 && r2 > 1e-4) {
          px /= r2;
          py /= r2;
        } else {
          return (d + 1) * 12.0;
        }
      }
      return -1;
    }

    case 'dragon_curve': {
      let zx = x0, zy = y0;
      const depth = Math.min(maxIter, 24);
      for (let d = 0; d < depth; d++) {
        const nextX = (zx - zy) * 0.70710678 + 0.1;
        const nextY = (zx + zy) * 0.70710678 + 0.1;
        zx = nextX; zy = nextY;
        if (zx * zx + zy * zy > 4.0) return (d + 1) * 15.0;
      }
      return -1;
    }

    case 'sierpinski_carpet': {
      let cx = Math.abs(x0);
      let cy = Math.abs(y0);
      const depth = Math.min(maxIter, 10);
      for (let d = 0; d < depth; d++) {
        const mx = Math.floor(cx * 3.0) % 3;
        const my = Math.floor(cy * 3.0) % 3;
        if (mx === 1 && my === 1) return (d + 1) * 15.0;
        cx = (cx * 3.0) % 1.0;
        cy = (cy * 3.0) % 1.0;
      }
      return -1;
    }

    case 'collatz': {
      let zx = x0, zy = y0, i = 0;
      while (zx * zx + zy * zy <= 100.0 && i < maxIter) {
        const piX = Math.PI * zx, piY = Math.PI * zy;
        const cosPiX = Math.cos(piX), sinPiX = Math.sin(piX);
        const coshPiY = Math.cosh(Math.min(10, Math.max(-10, piY)));
        const sinhPiY = Math.sinh(Math.min(10, Math.max(-10, piY)));
        const cR = cosPiX * coshPiY, cI = -sinPiX * sinhPiY;
        const tR = 1.0 + 2.0 * zx, tI = 2.0 * zy;
        const pR = tR * cR - tI * cI;
        const pI = tR * cI + tI * cR;
        zx = 0.25 * (1.0 + 4.0 * zx - pR);
        zy = 0.25 * (4.0 * zy - pI);
        i++;
      }
      return i >= maxIter ? -1 : i;
    }

    case 'lyapunov': {
      const a = Math.max(0.1, Math.min(4.0, x0));
      const b = Math.max(0.1, Math.min(4.0, y0));
      let x = 0.5;
      for (let s = 0; s < 50; s++) {
        const r = (s & 1) === 0 ? a : b;
        x = r * x * (1.0 - x);
      }
      let lambdaSum = 0.0;
      const count = Math.min(maxIter, 200);
      for (let s = 0; s < count; s++) {
        const r = (s & 1) === 0 ? a : b;
        x = r * x * (1.0 - x);
        const deriv = Math.abs(r * (1.0 - 2.0 * x));
        if (deriv > 1e-10) lambdaSum += Math.log(deriv);
      }
      const lambda = lambdaSum / count;
      return lambda < 0 ? Math.min(maxIter, Math.abs(lambda) * 50) : -1;
    }

    default:
      return -1;
  }
}

/**
 * High-speed buffer rendering for 2D Canvas / ArrayBuffer
 */
export function renderFractalBuffer({
  type,
  width,
  height,
  centerX,
  centerY,
  zoom,
  maxIterations,
  lut,
  params = {},
}) {
  const pixelBuffer = new Uint8ClampedArray(width * height * 4);
  const aspect = width / height;
  const spanY = 2.5 / zoom;
  const spanX = spanY * aspect;

  const minX = centerX - spanX / 2.0;
  const maxY = centerY + spanY / 2.0;
  const stepX = spanX / width;
  const stepY = spanY / height;

  const lutEntries = lut.length / 4;

  let offset = 0;
  for (let py = 0; py < height; py++) {
    const y0 = maxY - py * stepY;
    for (let px = 0; px < width; px++) {
      const x0 = minX + px * stepX;
      const iter = computePoint(type, x0, y0, maxIterations, params);

      if (iter === -1) {
        pixelBuffer[offset] = 5;
        pixelBuffer[offset + 1] = 6;
        pixelBuffer[offset + 2] = 12;
        pixelBuffer[offset + 3] = 255;
      } else {
        const lutIdx = Math.floor((iter * 14.5) % lutEntries) * 4;
        pixelBuffer[offset] = lut[lutIdx];
        pixelBuffer[offset + 1] = lut[lutIdx + 1];
        pixelBuffer[offset + 2] = lut[lutIdx + 2];
        pixelBuffer[offset + 3] = 255;
      }
      offset += 4;
    }
  }

  return pixelBuffer;
}
